import type { ReactNode } from 'react';
import { Button } from '@clay-run/glaze/button';
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogBody,
  AlertDialogHeader,
} from '@clay-run/glaze/alert-dialog';

/**
 * D22 (item 10) — the confirmation `AlertDialog` mount for a group-level
 * action, parameterized on copy/confirm-label/variant so a LATER unit (D21,
 * NOT built here) can reuse this exact component for "Clear all N" with
 * different copy and a `danger` confirm variant, rather than forking a
 * second dialog. Deliberately NOT Terra's pre-Glaze
 * `components/ConfirmDialog.tsx` (`Dialog`/`Modal`-based, pre-Glaze chrome)
 * — `PlaySection` is Glaze-built throughout, and mixing chrome systems
 * here would be exactly the inconsistency this QA round is fixing. Don't
 * "consolidate" this back onto `ConfirmDialog`.
 *
 * Controlled `AlertDialog` (open/onOpenChange), not the `useAlertDialog()`
 * hook: the hook needs a `DialogBaseProvider` ancestor
 * (`node_modules/@clay-run/glaze/dist/DialogBase-*.d.ts`), which nothing in
 * this repo mounts (grep-confirmed) — `PhotoIntake`'s "Scan N leads" confirm
 * uses the same controlled form for the same reason.
 *
 * KNOWN GLAZE GAP (Codex adversarial follow-up, D22 round, confirmed against
 * source, not a guess) — `body` renders via `AlertDialogBody`, a plain `div`
 * wrapper with no ARIA wiring. Glaze also exports an `AlertDialogDescription`
 * whose NAME implies it registers as the dialog's accessible description,
 * but reading the compiled source
 * (`node_modules/@clay-run/glaze/dist/DialogBase-*.js`) shows it is just a
 * differently-styled `Text` (default render `<p>`) — unlike
 * `AlertDialogTitle`, which DOES wrap base-ui's real `AlertDialog.Title` and
 * auto-registers for `aria-labelledby`, `AlertDialogDescription` never
 * touches base-ui's `AlertDialog.Description` primitive, so swapping to it
 * would buy NO `aria-describedby` wiring — and would actively break markup
 * here, since it defaults to `<p>` and `body` already nests its own `<Text>`
 * (`<p>`) children (invalid nested `<p>`). Net: there is no fix available
 * from this call site — `AlertDialogProps` exposes no prop to attach
 * `aria-describedby` to the Popup element Glaze renders internally. A
 * screen-reader rep opening this dialog hears the title but not the
 * irreversibility warning/skipped-count body until they navigate into it
 * manually. Flag for the Glaze adoption track (an upstream fix or an escape
 * hatch prop), not a Terra-side workaround around a third-party
 * component's internals.
 */
export function GroupActionConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  cancelLabel = 'Cancel',
  confirmLabel,
  confirmVariant = 'primary',
  confirmLoading,
  confirmDisabled,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: ReactNode;
  cancelLabel?: string;
  confirmLabel: string;
  /** @default 'primary' — D21's "Clear all N" is expected to pass `'danger'`. */
  confirmVariant?: 'primary' | 'danger';
  confirmLoading?: boolean;
  /**
   * Codex adversarial follow-up (D22 round) — the TRIGGER that opens this
   * dialog is disabled once its underlying count hits 0, but the dialog
   * itself can outlive a background refresh that drops the count to 0
   * WHILE open. Callers must pass their own zero-count check here so the
   * Confirm button can't fire a request the caller's own schema (e.g. a
   * `.min(1)` array) will reject.
   */
  confirmDisabled?: boolean;
  onConfirm: () => void;
}): ReactNode {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogHeader>{title}</AlertDialogHeader>
      <AlertDialogBody>{body}</AlertDialogBody>
      <AlertDialogActions>
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {cancelLabel}
        </Button>
        <Button variant={confirmVariant} loading={confirmLoading} disabled={confirmDisabled} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </AlertDialogActions>
    </AlertDialog>
  );
}
